import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Grid,
  SelectChangeEvent,
  Stack,
  Alert,
} from '@mui/material';
import { UserFormProps } from './user.form.interface';
import { di } from '../../state/inversify.config';
import {
  CreateUserInput,
  Role,
  UpdateUserInput,
  User,
} from '../../state/reduxRepositories/users/users.interfaces';
import {
  getRoleList,
  hasPermissionUpdateAdminFlags,
  validateNewUser,
} from './user.helpers';
import { UserQueryParamService } from './user.queryParam.service';
import { AccountTypes } from '../../components/changeUser/accountTypes.enum';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import { UserNotificationContext } from '../../components/userNotification/userNotificationContext';
import { UserNotificationType } from '../../components/userNotification/userNotificationType.enum';
import { TextInput } from '../../components/textInput/textInput';
import { SelectInput } from '../../components/select/selectInput';
import { Button } from '../../components/button/button';
import { Checkbox } from '../../components/checkBox/checkBox';
import { Spinner } from '../../components/spinner/spinner';
import { UserDomainService } from './userDomainService';

export function UserForm({ setMode, mode }: UserFormProps) {
  const { t } = useTranslation();

  const { addUserNotification } = useContext(UserNotificationContext);

  // TODO: move (ALL LOGIC) into a domainService and only return accountType or even better find a way so that you don't have to send down the loggedInUser

  const userQueryParamService = di.get<UserQueryParamService>(
    UserQueryParamService,
  );

  const userDomainService = di.get<UserDomainService>(UserDomainService);

  const users = userDomainService.useUserResourceSelector();
  const userErrorSelector = userDomainService.useUserErrorSelector();
  const useIsLoadingSelector = userDomainService.useLoadingResourceSelector();

  const queryParams = userQueryParamService.useSelectMergedQuery();

  const authService = di.get<AuthService>(AuthService);
  const loggedInUser = authService.useLoggedInUserSelector();
  const accountTypeRole =
    loggedInUser.accountType === AccountTypes.Org ? Role.USER : Role.MD;
  const currentUser = users.find((u) => u.id === queryParams.selectedUserId);

  const [currentFormValues, setCurrentFormValues] = useState<Partial<User>>({});
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (userErrorSelector?.length > 0) {
      // eslint-disable-next-line array-callback-return
      userErrorSelector.map((userError) => {
        if (
          userError.errorType &&
          userError.errorType.includes('UsernameExistsException')
        ) {
          setErrorMessage(t('EMAIL_ALREADY_EXISTS').toString());
        } else {
          setErrorMessage(t('GENERIC_USER_CREATE_ERROR').toString());
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userErrorSelector, useIsLoadingSelector, showAlert]);

  useEffect(() => {
    if (
      mode === 'edit' &&
      queryParams.selectedUserId !== currentFormValues?.id
    ) {
      if (currentUser) setCurrentFormValues({ ...currentUser });
    } else if (mode === 'create') {
      setCurrentFormValues({});
    }
  }, [
    users,
    queryParams.selectedUserId,
    mode,
    currentFormValues?.id,
    currentUser,
  ]);

  useEffect(() => {
    if (showAlert && !useIsLoadingSelector && userErrorSelector.length === 0) {
      addUserNotification(
        UserNotificationType.SUCCESS,
        mode === 'edit'
          ? t('USER_UPDATED_SUCCESSFULLY')
          : t('USER_CREATED_SUCCESSFULLY'),
        UserNotificationType.SUCCESS,
      );
      setMode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAlert, useIsLoadingSelector]);

  useEffect(() => {
    userErrorSelector.forEach((userError) => {
      setErrorMessage('');
      userDomainService.dispatchClearError(userError.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (e: SelectChangeEvent) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === 'email') {
      // eslint-disable-next-line no-param-reassign
      value = value.toLowerCase();
    }

    updateFormValue(name, value);
  };

  const updateFormValue = (name: string, value: string | boolean) => {
    setCurrentFormValues((previousValues) => {
      return {
        ...previousValues,
        [name]: value,
      };
    });
  };

  const handleSubmit = () => {
    const validationResult = validateNewUser(currentFormValues);
    // setErrorMessage([]);

    if (!validationResult.valid && validationResult.errorMessage) {
      setErrorMessage(validationResult.errorMessage);
      return;
    }

    if (mode === 'edit') {
      const userUpdateInput: UpdateUserInput = {
        id: currentFormValues.id as string,
      };
      // TODO: find a better way to only send changed form values that the current user is allowed to update.
      if (currentFormValues.role !== currentUser?.role) {
        userUpdateInput.role = currentFormValues.role;
      }
      if (currentFormValues.enabled !== currentUser?.enabled) {
        userUpdateInput.enabled = currentFormValues.enabled;
      }
      userDomainService.dispatchUpdate(userUpdateInput);
      setShowAlert(true);
    } else if (mode === 'create') {
      const newUserData = { ...currentFormValues };
      newUserData.role =
        currentFormValues.role !== undefined
          ? currentFormValues.role
          : accountTypeRole;
      newUserData.enabled = true;

      userDomainService.dispatchCreate({ ...newUserData } as CreateUserInput);
      setShowAlert(true);
    }
  };

  const setEnabledStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFormValue(event.currentTarget?.name, event.currentTarget?.checked);
  };

  const setDefaultRole = () => {
    if (currentUser?.role[0] === 'USER') {
      return 'USER';
    }
    if (currentUser?.role[0] === 'ADMIN') {
      return 'ADMIN';
    }
    return '';
  };

  return (
    <Container maxWidth="md">
      <Box component="form" onSubmit={handleSubmit}>
        <Grid item>
          <TextInput
            fullWidth
            type="text"
            name="email"
            label={`${t('EMAIL')}`}
            onChange={handleFieldChange}
            value={currentFormValues?.email || ''}
            disabled={mode === 'edit'}
            sx={{ mt: 1 }}
            required
          />
          <TextInput
            fullWidth
            type="text"
            name="firstName"
            label={`${t('FIRST_NAME')}`}
            onChange={handleFieldChange}
            value={currentFormValues?.firstName || ''}
            disabled={mode === 'edit'}
            sx={{ mt: 1 }}
            required
          />
          <TextInput
            fullWidth
            type="text"
            name="lastName"
            label={`${t('LAST_NAME')}`}
            onChange={handleFieldChange}
            value={currentFormValues?.lastName || ''}
            disabled={mode === 'edit'}
            required
            sx={{ my: 2 }}
          />
          <SelectInput
            fullWidth
            label={t('ROLE')}
            name="role"
            items={getRoleList(loggedInUser)}
            onChange={handleFieldChange}
            disabled={hasPermissionUpdateAdminFlags(
              loggedInUser,
              currentFormValues?.id,
            )}
            defaultValue={setDefaultRole}
            required
          />

          {mode === 'edit' && (
            <Checkbox
              testId="userStatus"
              name="enabled"
              label={t('ENABLED')}
              checked={currentFormValues?.enabled}
              onChange={setEnabledStatus}
            />
          )}
          {errorMessage && (
            <Stack sx={{ width: '100%', mt: 2 }}>
              <Alert severity="error" onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            </Stack>
          )}
          {useIsLoadingSelector && (
            <Spinner
              open={useIsLoadingSelector}
              text={
                mode === 'create'
                  ? `${t('SAVING_USER')}`
                  : `${t('UPDATING_USER')}`
              }
            />
          )}

          <Button
            color="secondary"
            testId="saveUser"
            type="submit"
            disabled={useIsLoadingSelector}
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            label={t('SAVE')}
          />
        </Grid>
      </Box>
    </Container>
  );
}
